import React, { useEffect } from "react";
import { EditOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { Button, Form, Image, Input, Popconfirm } from "antd";
import TextArea from "antd/es/input/TextArea";

import ButtonWrapper from "../../../components/reuseComp/ButtonWrapper";

interface contentItem {
  title: string;
  desc: string;
}

interface ourPrcessProps {
  heading?: string;
  subtext?: string;
  content?: contentItem[];
}

interface Props {
  isEditing: boolean;
  setIsEditing: (v: boolean) => void;
  handleCancel: () => void;
  handleSave: () => void;
  onDelete: (index: number, field: "Our Process") => void;
  form: any;
  ourProcessData?: ourPrcessProps;
  loading?: boolean;
  mode: "list" | "add" | "edit";
  setMode: (value: "list" | "add" | "edit") => void;
  editingIndex: number | null;
  setEditingIndex: (value: number | null) => void;
}

const OurProcess: React.FC<Props> = ({
  isEditing,
  setIsEditing,
  handleCancel,
  handleSave,
  form,
  ourProcessData,
  onDelete,
  loading,
  mode,
  setMode,
  editingIndex,
  setEditingIndex,
}) => {
  useEffect(() => {
    if (!ourProcessData) return;

    form.resetFields(["ourProcess", "content"]);

    if (mode === "list") {
      form.setFieldsValue({
        ourProcess: {
          heading: ourProcessData.heading || "",
          subtext: ourProcessData.subtext || "",
          content: ourProcessData.content || [],
        },
      });
    }

    if (mode === "add") {
      form.setFieldsValue({
        ourProcess: {
          content: [{ title: "", desc: "" }],
        },
      });
    }

    if (mode === "edit" && editingIndex !== null) {
      const item = ourProcessData.content?.[editingIndex];
      if (item) {
        form.setFieldsValue({
          ourProcess: {
            content: [{ title: item.title, desc: item.desc }],
          },
        });
      }
    }
  }, [mode, editingIndex, ourProcessData]);

  return (
    <div>
      {isEditing && (
        <div className="bg-[#F9F9F9] p-5 rounded-xl mb-4">
          <Form layout="vertical" form={form}>
            <Form.Item
              label="Heading"
              name={["ourProcess", "heading"]}
              rules={[{ required: true, message: "Enter heading" }]}
            >
              <Input placeholder="enter heading..."/>
            </Form.Item>

            <Form.Item
              label="Subtext"
              name={["ourProcess", "subtext"]}
              rules={[{ required: true, message: "Enter subtext" }]}
            >
              <TextArea rows={3} placeholder="enter subtext..."/>
            </Form.Item>
          </Form>

          <div className="flex gap-3 mt-4">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={loading}
            >
              Save
            </ButtonWrapper>
            <ButtonWrapper
              onClick={() => {
                setIsEditing(false);
                handleCancel();
              }}
            >
              Cancel
            </ButtonWrapper>
          </div>
        </div>
      )}

      {mode === "list" && !isEditing && (
        <div className="rounded-[20px] overflow-hidden">
          <div className="hidden sm:block">
            <table className="min-w-full">
              <thead>
                <tr className="bg-primary text-white">
                  <th className="py-3 px-4 text-left">Heading</th>
                  <th className="py-3 px-4 text-left">Subtext</th>
                  <th className="py-3 px-4 text-right">Action</th>
                </tr>
              </thead>

              <tbody className="bg-[#F9F9F9]">
                {ourProcessData?.heading || ourProcessData?.subtext ? (
                  <tr className="border-b">
                    <td className="px-4 py-3">{ourProcessData?.heading}</td>
                    <td className="px-4 py-3">{ourProcessData?.subtext}</td>
                    <td className="px-4 py-3 text-right">
                      <EditOutlined
                        className="text-primary text-xl cursor-pointer"
                        onClick={() => setIsEditing(true)}
                      />
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td colSpan={3} className="text-center py-6">
                      <Button type="dashed" onClick={() => setIsEditing(true)}>
                        Add Data
                      </Button>
                    </td>
                  </tr>
                )}

                {/* Add Steps */}
                <tr>
                  <td colSpan={3} className="px-4 py-3 text-right">
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        setMode("add");
                        setEditingIndex(null);
                      }}
                      className="text-primary font-semibold"
                    >
                      Add Steps
                    </Button>
                  </td>
                </tr>

                {/* Steps Table */}
                <tr>
                  <td colSpan={3} className="p-4">
                    <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="px-4 py-2 text-left">Title</th>
                          <th className="px-4 py-2 text-left">Description</th>
                          <th className="px-4 py-2 text-right">Action</th>
                        </tr>
                      </thead>

                      <tbody>
                        {ourProcessData?.content?.length ? (
                          ourProcessData.content.map((item, index) => (
                            <tr key={index} className="border-t">
                              <td className="px-4 py-2">{item.title}</td>
                              <td className="px-4 py-2">{item.desc}</td>
                              <td className="px-4 py-2 text-right">
                                <div className="flex justify-end gap-4 text-lg">
                                  <EditOutlined
                                    className="cursor-pointer text-primary"
                                    onClick={() => {
                                      setMode("edit");
                                      setEditingIndex(index);
                                    }}
                                  />
                                  <Popconfirm
                                    title="Are you sure to delete this?"
                                    onConfirm={() =>
                                      onDelete(index, "Our Process")
                                    }
                                  >
                                    <DeleteOutlined className="cursor-pointer text-red-500" />
                                  </Popconfirm>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={3}
                              className="px-4 py-6 text-center text-gray-500"
                            >
                              No steps added
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="sm:hidden space-y-4">
            <div className="bg-[#F9F9F9] p-4 rounded-xl shadow-sm space-y-3">
              <div>
              <div><b className="text-primary">Heading:</b></div>
                <p>{ourProcessData?.heading || "—"}</p>
              </div>
              <div>
              <div><b className="text-primary">Subtext:</b></div>
                <p>{ourProcessData?.subtext || "—"}</p>
              </div>

              <div className="flex justify-end mt-2 text-primary">
                <EditOutlined
                  className="cursor-pointer text-lg"
                  onClick={() => setIsEditing(true)}
                />
              </div>
            </div>

            <div className="text-right">
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => {
                  setMode("add");
                  setEditingIndex(null);
                }}
              >
                Add Steps
              </Button>
            </div>

            {ourProcessData?.content?.length ? (
              ourProcessData.content.map((item, index) => (
                <div
                  key={index}
                  className="bg-[#F9F9F9] p-4 rounded-xl shadow-sm space-y-3"
                >
                  <div>
                    <span className="font-semibold text-primary">Title:</span>
                    <p className="text-sm">{item.title}</p>
                  </div>
                  <div>
                    <span className="font-semibold text-primary">Description:</span>
                    <p className="text-sm">{item.desc}</p>
                  </div>

                  <div className="flex justify-end gap-4 mt-2 text-primary text-[20px]">
                    <EditOutlined
                      className="cursor-pointer"
                      onClick={() => {
                        setMode("edit");
                        setEditingIndex(index);
                      }}
                    />
                    <Popconfirm
                      title="Are you sure to delete this?"
                      onConfirm={() => onDelete(index, "Our Process")}
                    >
                      <DeleteOutlined className="cursor-pointer text-red-500" />
                    </Popconfirm>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 py-6">
                No steps added
              </div>
            )}
          </div>
        </div>
      )}

      {mode !== "list" && !isEditing && (
        <div className="bg-[#F9F9F9] p-5 rounded-xl">
          <Form layout="vertical" form={form}>
            <Form.List name={["ourProcess", "content"]}>
              {(fields) => (
                <>
                  {fields.map((field) => (
                    <div key={field.key} className="border p-4 rounded mb-4">
                      <Form.Item
                        label="Title"
                        name={[field.name, "title"]}
                        rules={[{ required: true, message: "Enter title" }]}
                      >
                        <Input placeholder="enter title..."/>
                      </Form.Item>

                      <Form.Item
                        label="Description"
                        name={[field.name, "desc"]}
                        rules={[
                          { required: true, message: "Enter description" },
                        ]}
                      >
                        <TextArea rows={2} placeholder="enter decription..."/>
                      </Form.Item>
                    </div>
                  ))}
                </>
              )}
            </Form.List>
          </Form>

          <div className="flex gap-3 mt-4">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={loading}
            >
              Save
            </ButtonWrapper>
            <ButtonWrapper
              onClick={() => {
                handleCancel();
                setMode("list");
                setEditingIndex(null);
              }}
            >
              Cancel
            </ButtonWrapper>
          </div>
        </div>
      )}
    </div>
  );
};

export default OurProcess;
